import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NotificationsService, PushNotificationService, WebSocketService } from '@org/shared-services';
import { Notifications } from '@org/interfaces';
import { Observable, Subscription } from 'rxjs';
import { AuthService, TaskService } from '@org/shared-services';
import { MatSnackBar } from '@angular/material/snack-bar';
import { isBadgingSupported } from '@org/utilities';

@Component({
    selector: 'app-notification',
    imports: [CommonModule],
    templateUrl: './notifications.component.html',
    styleUrl: './notifications.component.css'
})
export class NotificationsComponent implements OnInit {

  notifications: Notifications[] = [];
  lastSeenNotifications: Notifications[] = [];
  unreadCount = 0;
  notificationsVisible = false;
  notifications$!: Observable<Notifications[]>;
  totalNotifications$!: Observable<number>;
  slideOutVisible = false;
  timeUpdateSubscription!: Subscription;
  previousTotal = 0;

  constructor(private notificationsService: NotificationsService, private authService: AuthService,
    private taskService: TaskService, private pn: PushNotificationService,
    private ws: WebSocketService,
    private snackBar: MatSnackBar, private cdr: ChangeDetectorRef) {
    this.notifications$ = this.notificationsService.notifications$;
    this.totalNotifications$ = this.notificationsService.totalNotifications$;
  }

  async ngOnInit(): Promise<void> {
    if (this.authService.isLoggedIn()) {
      this.ws.connect();
      this.loadNotifications();
      this.totalNotifications$.subscribe(total => {
        if (this.authService.isLoggedIn()) {
          this.unreadCount = total;
          this.previousTotal = total;
          this.taskService.triggerTaskUpdate();
          this.cdr.markForCheck();
        }
      });
    }

    const io = this.ws.getIo();
    if (io) {
      io.on('privateMessage', (data: any) => {
        if (data.type === 'notification' && this.authService.isLoggedIn()) {
          this.notificationsService.addNotification(data.message);
        }
      });
    }
  }

  loadNotifications(): void {
    this.notificationsService.getNotifications();
    this.notifications$.subscribe({
      next: (notifications) => {
        this.unreadCount = notifications.filter(n => !n.read).length;
        const newNotifications = notifications.filter((n) => !n.read);
        const lastSeenNewNotifications = this.lastSeenNotifications.filter((n) => !n.read);

        if (newNotifications.length > lastSeenNewNotifications.length) {
          this.taskService.triggerTaskUpdate();
          this.showToast('You have a new notification!');
        }
        this.lastSeenNotifications = [...notifications];
        this.notifications = notifications;
      },
      error: (error) => {
        console.error('Error loading notifications', error);
      }
    });
  }

  markAsRead(notificationId: string): void {
    this.notificationsService.markAsRead(notificationId).subscribe({
      next: () => {
        this.loadNotifications();
      },
      error: (error) => {
        console.error('Error marking notification as read', error);
      }
    });
  }

  markAllAsRead(): void {
    this.notificationsService.markAllAsRead().subscribe(() => {
      this.taskService.triggerTaskUpdate();
      this.cdr.markForCheck();
    });
  }

  toggleNotifications(): void {
    this.toggleSlideNotifications();
    if (this.notificationsVisible) {
      this.loadNotifications();
    }
  }

  toggleSlideNotifications(): void {
    this.slideOutVisible = !this.slideOutVisible;
  }

  isTech(): boolean {
    return !this.authService.hasRole('tech');
  }

  reloadView(): void {
    this.loadNotifications();
  }

  showToast(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 3000,
      verticalPosition: 'top',
      horizontalPosition: 'right',
      panelClass: ['custom-snackbar-container']
    });
  }

  onNewNotificationReceived(message: string): void {
    if (isBadgingSupported()) {
      this.notificationsService.incrementUnreadCount(this.unreadCount);
    }
  }

  openDetails(notification: any): void {
    this.notificationsService.openSlideOut(notification);
    this.markAsRead(notification._id);
  }
}
